import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, Checkbox, Chip, Dialog, DialogContent, FormControlLabel, Typography } from '@mui/material'
import { isNil } from 'lodash'
import { toast } from 'react-toastify'
import { BasicDialogAction, BasicDialogTitle } from 'common/components/modal/DialogComponents'
import DevLotsAction from 'common/actions/DevLotsAction'
import { getTextColorByHexColor, PASTEL_COLOR } from 'common/utils/ColorUtil'
import { computeLotsSync, LOT_CATEGORIE_ITEMS, LOT_STATUT, LOT_STATUT_ITEMS, ticketsLabel } from './DevLotsUtils'

const getStateLabel = ({ categorie, statut }) => {
    const categorieLabel = LOT_CATEGORIE_ITEMS.find(c => c.value === categorie)?.label
    const statutLabel = LOT_STATUT_ITEMS.find(s => s.value === statut)?.label
    return statutLabel ? `${categorieLabel} — ${statutLabel}` : categorieLabel
}

const getOuverture = (target, current) => (target.statut === LOT_STATUT.EN_COURS && isNil(current) ? Date.now() : current)

const ACTIONS = {
    create: { label: 'Créer le lot', color: PASTEL_COLOR.green },
    state: { label: 'Modifier le lot', color: PASTEL_COLOR.orange },
    assign: { label: 'Ajouter des tickets au lot', color: '#8ab4f8' },
}

const GROUPS = [
    { unprefixed: false, title: 'Lots avec préfixe dans Mantis (0. à 4.)', hint: 'Cochés par défaut.' },
    { unprefixed: true, title: 'Lots sans préfixe dans Mantis', hint: 'Traités comme archivés (« 3. »), décochés par défaut : à relire avant d’appliquer.' },
]

// Une ligne par ticket : état des lieux Mantis / Aquaparc, puis ce qui sera fait.
const getTicketLine = ({ id, from }, lotName) => ({
    id,
    mantis: `lot « ${lotName} »`,
    aquaparc: from ? `lot « ${from} »` : 'aucun lot',
    action: from ? `retiré du lot « ${from} » et ajouté au lot « ${lotName} »` : `ajouté au lot « ${lotName} »`,
})

// name : lot concerné ; mantis / aquaparc : état des lieux ; action : ce qui sera fait ; tickets : détail par ticket.
const describe = (diff) => {
    switch (diff.type) {
        case 'create':
            return {
                name: diff.nom,
                mantis: `lot présent (${getStateLabel(diff)}), ${ticketsLabel(diff.tickets.length)}`,
                aquaparc: 'lot absent',
                action: `Le lot sera créé (${getStateLabel(diff)}), puis les tickets ci-dessous y seront ajoutés.`,
                tickets: diff.tickets.map(t => getTicketLine(t, diff.nom)),
            }
        case 'state':
            return {
                name: diff.devLot.nom,
                mantis: `lot ${getStateLabel(diff)}`,
                aquaparc: `lot ${getStateLabel(diff.devLot)}`,
                action: `Le lot passera de « ${getStateLabel(diff.devLot)} » à « ${getStateLabel(diff)} » (les tickets ne changent pas).`,
                tickets: [],
            }
        default:
            return {
                name: diff.devLot.nom,
                mantis: `${ticketsLabel(diff.tickets.length)} dans ce lot`,
                aquaparc: `${diff.tickets.length > 1 ? 'ces tickets sont absents' : 'ce ticket est absent'} du lot`,
                action: `${diff.tickets.length > 1 ? 'Ces tickets seront ajoutés' : 'Ce ticket sera ajouté'} au lot (liste ci-dessous).`,
                tickets: diff.tickets.map(t => getTicketLine(t, diff.devLot.nom)),
            }
    }
}

const applyDiff = (diff) => {
    if (diff.type === 'create') {
        const { nom, categorie, statut, ticketIds } = diff
        return DevLotsAction.createQuiet({ nom, categorie, statut, ouverture: getOuverture(diff, undefined), tickets: ticketIds })
    }
    if (diff.type === 'state') {
        const { categorie, statut } = diff
        return DevLotsAction.updateQuiet({ ...diff.devLot, categorie, statut, ouverture: getOuverture(diff, diff.devLot.ouverture) })
    }
    return Promise.all(diff.ticketIds.map(id => DevLotsAction.assign(id, diff.devLot.id)))
        .then(results => (results.every(r => !isNil(r)) ? true : undefined))
}

// Compare le champ « Lot » de Mantis aux lots Aquaparc et applique, au choix, les différences (Mantis fait foi).
const DialogSyncLots = ({ mantisTickets = [], devLots = [], onClose = () => {}, onDone = () => {} }) => {
    const diffs = useMemo(() => computeLotsSync(mantisTickets, devLots), [mantisTickets, devLots])
    const [selected, setSelected] = useState(() => diffs.filter(d => !d.unprefixed).map(d => d.id))
    const [isSaving, setIsSaving] = useState(false)

    const toggle = (id) => setSelected(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]))
    const setGroup = (groupDiffs, checked) => setSelected(prev => {
        const others = prev.filter(id => !groupDiffs.some(d => d.id === id))
        return checked ? [...others, ...groupDiffs.map(d => d.id)] : others
    })
    const toApply = diffs.filter(d => selected.includes(d.id))

    const validate = () => {
        setIsSaving(true)
        Promise.all(toApply.map(applyDiff))
            .then(results => {
                const nbFailed = results.filter(r => isNil(r)).length
                if (nbFailed === 0) {
                    toast.success(`${toApply.length} modification${toApply.length > 1 ? 's' : ''} appliquée${toApply.length > 1 ? 's' : ''}`)
                } else {
                    toast.warning(`${toApply.length - nbFailed} appliquée(s), ${nbFailed} en erreur`)
                }
                onDone()
            })
            .finally(() => setIsSaving(false))
    }

    return (
        <Dialog open maxWidth='md' fullWidth>
            <BasicDialogTitle onClose={onClose}>Synchroniser les lots depuis Mantis</BasicDialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 13, color: '#6b7893', mb: 2 }}>
                    Compare le champ « Lot » de Mantis aux lots Aquaparc. Mantis fait foi : chaque ligne cochée sera appliquée dans Aquaparc. Aucun ticket n’est retiré d’un lot et aucun lot n’est supprimé.
                </Typography>
                {diffs.length === 0 && <Typography sx={{ py: 2 }}>Les lots Aquaparc sont à jour avec Mantis.</Typography>}
                {GROUPS.map(({ unprefixed, title, hint }) => {
                    const groupDiffs = diffs.filter(d => d.unprefixed === unprefixed)
                    if (groupDiffs.length === 0) {
                        return null
                    }
                    return (
                        <Box key={title} sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontWeight: 700, color: '#1c2340' }}>{`${title} (${groupDiffs.length})`}</Typography>
                                <Box sx={{ flex: 1 }} />
                                <Button size='small' onClick={() => setGroup(groupDiffs, true)}>Tout cocher</Button>
                                <Button size='small' onClick={() => setGroup(groupDiffs, false)}>Tout décocher</Button>
                            </Box>
                            <Typography sx={{ fontSize: 12, color: '#6b7893', mb: 0.5 }}>{hint}</Typography>
                            {groupDiffs.map(diff => {
                                const { name, mantis, aquaparc, action, tickets } = describe(diff)
                                const { label, color } = ACTIONS[diff.type]
                                return (
                                    <FormControlLabel
                                        key={diff.id}
                                        sx={{ display: 'flex', alignItems: 'flex-start', m: 0, py: 0.5, borderTop: '1px solid #e6eaf2' }}
                                        control={<Checkbox size='small' checked={selected.includes(diff.id)} onChange={() => toggle(diff.id)} />}
                                        label={(
                                            <Box sx={{ pt: '4px' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip size='small' label={label} sx={{ backgroundColor: color, color: getTextColorByHexColor(color), fontWeight: 600 }} />
                                                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{name}</Typography>
                                                </Box>
                                                <Box sx={{ fontSize: 12, color: '#6b7893', mt: 0.5, wordBreak: 'break-word' }}>
                                                    <div><b>Mantis :</b> {mantis}</div>
                                                    <div><b>Aquaparc :</b> {aquaparc}</div>
                                                    <div style={{ color: '#1c2340' }}><b>→ Sera fait :</b> {action}</div>
                                                    {tickets.map(t => (
                                                        <div key={t.id} style={{ paddingLeft: 12 }}>
                                                            {`#${t.id} — Mantis : ${t.mantis} · Aquaparc : ${t.aquaparc} → ${t.action}`}
                                                        </div>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    />
                                )
                            })}
                        </Box>
                    )
                })}
            </DialogContent>
            {diffs.length > 0 && (
                <BasicDialogAction
                    buttonLabel={`Appliquer (${toApply.length})`}
                    onValidate={validate}
                    isLoading={isSaving}
                    isDisabled={toApply.length === 0}
                />
            )}
        </Dialog>
    )
}

DialogSyncLots.propTypes = {
    mantisTickets: PropTypes.arrayOf(PropTypes.shape({})),
    devLots: PropTypes.arrayOf(PropTypes.shape({})),
    onClose: PropTypes.func,
    onDone: PropTypes.func,
}

export default DialogSyncLots
